import type { AnimationDirection } from '../productImageGallery/ProductImageGallery';
import type { ReactElement } from 'react';
import type { RuleSet } from 'styled-components';

import Image from 'next/image';
import { useRef, useState, useEffect, useCallback, useMemo, memo } from 'react';
import styled, { css } from 'styled-components';

import { media } from '../../../shared/core/styles';
import type { ProductColor as ProductColorSchema } from '~/amplienceTypes/schemas/imported/product-color-schema';
import { FesLink } from '~/components/shared/core/link/FesLink';
import { imageBlurData } from '~/utilities/constants/base64Images/imageBlurData';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Color, Maybe, ProductColorData } from '~/utilities/graphql/codegen';
import { SiteType } from '~/utilities/graphql/codegen';
import { getColorDataAttributes } from '~/utilities/helpers/getColorDataAttributes';

export type ProductStyleVariantsStateProps = {
  color?: Color | null;
  colorData?: Maybe<ProductColorData>;
};

const ItemBorderCircleCss = css`
  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background: transparent;
    position: absolute;
    box-shadow: ${({ theme }) => `inset 0 0 0 2px ${theme.colors.neutral['0']}`};
    z-index: 100;
    border-radius: 50%;
  }
`;

const GstarColorSelection = css`
  margin: ${({ theme }) => theme.spacing.y.sm} 0 ${({ theme }) => theme.spacing.y.lg};

  @media ${({ theme }) => media(theme).greaterThan('sm')} {
    margin-bottom: 32px;
  }
`;
const AafColorSelection = css`
  margin: 0;
`;

const GstarLinkLabelSelection = css`
  font-weight: ${({ theme }) => theme.variables.font.weight.medium};
  color: ${({ theme }) => theme.colors.content.primary};
  vertical-align: bottom;
  text-transform: uppercase;
`;
const AafLinkLabelSelection = css`
  font-weight: ${({ theme }) => theme.variables.font.weight.bold};
  vertical-align: bottom;
  text-transform: capitalize;
`;

const GstarColorName = css`
  font-weight: ${({ theme }) => theme.variables.font.weight.regular};
  color: ${({ theme }) => theme.colors.content['brand-accent']};
  vertical-align: bottom;
  text-decoration: underline;
`;
const AafColorName = css``;

const ColorNameConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarColorName,
  [SiteType.Wahts]: GstarColorName,
  [SiteType.Outlet]: GstarColorName,
  [SiteType.Employeeshop]: GstarColorName,
  [SiteType.Aaf]: AafColorName,
};

const ColorSelectionConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarColorSelection,
  [SiteType.Wahts]: GstarColorSelection,
  [SiteType.Outlet]: GstarColorSelection,
  [SiteType.Employeeshop]: GstarColorSelection,
  [SiteType.Aaf]: AafColorSelection,
};

const LinkLabelConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarLinkLabelSelection,
  [SiteType.Wahts]: GstarLinkLabelSelection,
  [SiteType.Outlet]: GstarLinkLabelSelection,
  [SiteType.Employeeshop]: GstarLinkLabelSelection,
  [SiteType.Aaf]: AafLinkLabelSelection,
};

const WahtsCarouselInnerItemStyle = css<{ $isSelected: boolean; $isHovered: boolean }>`
  border-radius: ${({ theme }) => theme.borders.radius.circular};
  background-color: ${({ theme }) => theme.colors.neutral['50']};

  --border-width: ${({ $isSelected, $isHovered, theme }) => {
    if ($isSelected) {
      return '2px';
    }

    if ($isHovered) {
      return theme.borders.width.xs;
    }

    return '0px';
  }};
  --border-color: ${({ $isSelected, $isHovered, theme }) => {
    if ($isSelected) {
      return theme.colors.content.primary;
    }

    if ($isHovered) {
      return theme.colors.neutral['500'];
    }

    return 'transparent';
  }};

  &:has(a:focus-visible) {
    border-width: 2px;
    border-color: ${({ theme }) => theme.colors.content.primary};
    box-shadow: ${({ theme }) => `0 0 0 3px ${theme.colors.blue['100']}`};
  }

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      &:hover,
      &:focus {
        border-width: ${theme.borders.width.sm};
        border-color: ${theme.colors.neutral['500']};
      }
    `}
`;

const WahtsCarouselItemCheckIconStyle = css`
  color: ${({ theme }) => theme.colors.content.primary};
  backdrop-filter: blur(4px);
`;

const CarouselItemCheckIconConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Employeeshop]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Wahts]: WahtsCarouselItemCheckIconStyle,
};

const CarouselInnerItemConfig: Record<
  SiteType,
  RuleSet<{ $isSelected: boolean; $isHovered: boolean }>
> = {
  [SiteType.Gstar]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Employeeshop]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Wahts]: WahtsCarouselInnerItemStyle,
};

const styles = {
  Text: css`
    color: ${({ theme }) => theme.colors.content.primary};
    font-size: ${({ theme }) => theme.font.size[2]};
    line-height: 1.4;
    text-transform: none;
  `,
  ArrowButton: css`
    cursor: pointer;
    display: none;
    position: absolute;
    height: ${({ theme }) => theme.font.size['7']};
    top: 50%;
    transform: translateY(-50%);
    text-align: center;
    z-index: 2;
    background: ${({ theme }) =>
      `${theme.colors.background.component['carousel-arrow'].rest} url(${imageBlurData}) 0 0 repeat scroll`};
    border-radius: ${({ theme }) => theme.borders.radius.xs};

    &::before {
      font-family: gstar-icons;
      display: inline-block;
      font-style: normal;
      font-weight: ${({ theme }) => theme.variables.font.weight.bold};
      width: ${({ theme }) => theme.font.size['7']};
      border-radius: ${({ theme }) => theme.borders.radius.xs};
      line-height: ${({ theme }) => theme.font.size['7']};
      text-decoration: inherit;
      text-align: center;
      font-variant: normal;
      text-transform: none;
      font-size: ${({ theme }) => theme.font.size[4]};
      color: ${({ theme }) => theme.colors.content.inverse};
    }

    &:hover {
      &::before {
        background-color: ${({ theme }) =>
          theme.colors.background.component['carousel-arrow'].hover};
      }
    }

    &:active {
      &::before {
        background-color: ${({ theme }) =>
          theme.colors.background.component['carousel-arrow'].pressed};
      }
    }

    &:focus,
    &:focus-visible {
      outline: 1px solid;
      outline-color: ${({ theme }) => theme.colors.background.component['carousel-arrow'].focussed};
      outline-offset: 1px;
    }
  `,
  CarouselItemCheckIcon: css`
    content: '${'\\F253'}';
    font-family: gstar-icons;
    position: absolute;
    width: ${({ theme }) => theme.font.size['6']};
    height: ${({ theme }) => theme.font.size['6']};
    line-height: ${({ theme }) => theme.font.size['6']};
    border-top: none;
    border-left: none;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    background: ${({ theme }) => theme.colors.neutral['0-70']};
    color: ${({ theme }) => theme.colors.content.component.dark};
    font-size: ${({ theme }) => theme.font.size['3']};
    font-weight: ${({ theme }) => theme.variables.font.weight.bold};
    cursor: pointer;
    text-align: center;
  `,
};

const S = {
  ColorSelection: styled.div`
    --item-size: 60px;

    display: flex;
    flex-direction: column;
    ${({ theme }) => ColorSelectionConfig[theme.siteType]}
  `,
  ColorDescriptionWrapper: styled.div`
    display: flex;
    font-size: ${({ theme }) => theme.font.size[2]};
    padding-bottom: ${({ theme }) => theme.padding.xs};

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      padding-bottom: ${({ theme }) => theme.padding['2xs']};
    }
  `,
  Anchor: styled(FesLink)`
    ${styles.Text};
    text-decoration: none;
  `,
  LinkLabel: styled.span`
    padding-right: ${({ theme }) => theme.padding['2xs']};
    ${({ theme }) => LinkLabelConfig[theme.siteType]}
  `,
  ColorName: styled.span`
    ${({ theme }) => ColorNameConfig[theme.siteType]}
  `,
  Carousel: styled.section`
    position: relative;
  `,
  CarouselContainer: styled.div`
    scroll-behavior: smooth;
    scrollbar-width: none;
    display: flex;
    align-items: center;
    margin: -3px 0;
    padding: 3px 0;
    overflow-x: auto;
    overflow-y: hidden;
    touch-action: pan-x;

    &::-webkit-scrollbar {
      width: 0;
    }

    &:has([data-testid='style-carousel-item']:first-child a:focus-visible) {
      margin-left: -3px;
      padding-left: 3px;
    }

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      min-height: calc(var(--item-size) + 6px);
    }
  `,
  CarouselInner: styled.ul<{ $itemCount: number }>`
    display: grid;
    grid-template-columns: repeat(${({ $itemCount }) => $itemCount}, var(--item-size));
    grid-gap: 4px;
    padding: 0;
    margin: 0;
    list-style: none;
  `,
  CarouselInnerItem: styled.li<{ $isSelected: boolean; $isHovered: boolean }>`
    --border-width: ${({ $isSelected }) => ($isSelected ? '2px' : '1px')};
    --border-color: ${({ $isSelected, $isHovered, theme }) => {
      if ($isSelected) {
        return theme.colors.content.component.dark;
      }

      if ($isHovered) {
        return theme.colors.slate['500'];
      }

      return theme.colors.border.inverse;
    }};

    width: var(--item-size);
    height: var(--item-size);
    position: relative;
    border-radius: 50%;
    overflow: hidden;
    border: var(--border-width) solid var(--border-color);
    box-sizing: border-box;
    background-color: transparent;
    transition: 0.3s background-color linear;
    clip-path: circle(50%);

    &:has(a:focus-visible) {
      border-width: 2px;
      border-color: ${({ theme }) => theme.colors.content.focus};
      box-shadow: ${({ theme }) => `0 0 0 2px ${theme.colors.blue['100']}`};
      clip-path: unset;

      ${ItemBorderCircleCss}
    }

    ${({ $isSelected, theme }) =>
      $isSelected &&
      css`
        &:has(a:focus-visible) {
          border-color: ${theme.colors.content.component.dark};
          box-shadow: ${`0 0 0 2px ${theme.colors.blue['100']}`},
            ${`0 0 0 4px ${theme.colors.content.focus}`};
        }

        &:hover,
        &:focus {
          border-color: ${theme.colors.slate['500']};
        }

        ${ItemBorderCircleCss}
      `}

    ${({ theme }) => CarouselInnerItemConfig[theme.siteType]}

    @media (prefers-reduced-motion) {
      transition: none;
    }
  `,
  CarouselAnchor: styled(FesLink)<{ $isSelected: boolean }>`
    display: inline-block;
    width: 100%;
    height: 100%;
    position: relative;

    img {
      opacity: ${({ $isSelected }) => ($isSelected ? '0.8' : '1')};
      object-fit: cover;
    }

    &::after {
      ${({ $isSelected }) => $isSelected && styles.CarouselItemCheckIcon}
      ${({ $isSelected, theme }) => $isSelected && CarouselItemCheckIconConfig[theme.siteType]}
    }
  `,

  ArrowLeft: styled.button<{
    $activeArrow: AnimationDirection | 'all' | undefined;
  }>`
    ${styles.ArrowButton};
    left: -28px;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      display: ${({ $activeArrow }) =>
        $activeArrow === 'left' || $activeArrow === 'all' ? 'block' : 'none'};
    }

    &::before {
      content: '${'\\F255'}';
    }
  `,
  ArrowRight: styled.button<{
    $activeArrow: AnimationDirection | 'all' | undefined;
  }>`
    ${styles.ArrowButton};
    right: -28px;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      display: ${({ $activeArrow }) =>
        $activeArrow === 'right' || $activeArrow === 'all' ? 'block' : 'none'};
    }

    &::before {
      content: '${'\\F256'}';
    }
  `,
};

const convertImageUrl = (url: string) => url.replace('{{dimensions}}', 'h_136');

const ProductColorComponent = ({
  colourV2,
  color,
  colorData,
}: ProductStyleVariantsStateProps & ProductColorSchema): ReactElement => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeArrow, setActiveArrow] = useState<AnimationDirection | 'all' | undefined>(undefined);
  const [hoverDisabled, setHoverDisabled] = useState(false);

  const { productStyleVariantIndex, styleVariants } = useGalleryContext();

  // Using styleVariantsHoveredIndex from GalleryContext causes
  // entire page recalculation. Preserve the local state
  const [styleVariantsHoveredIndex, hoverColorCarouselIndex] =
    useState<number>(productStyleVariantIndex);

  const { accessibilityLabels } = useStaticContext();

  const handleMouseLeave = useCallback(() => {
    hoverColorCarouselIndex(productStyleVariantIndex);
    setHoverDisabled(false);
  }, [hoverColorCarouselIndex, productStyleVariantIndex]);

  const handleScroll = useCallback(() => {
    setHoverDisabled(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setHoverDisabled(false);
    }, 50);
  }, []);

  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (hoverDisabled) {
      return;
    }

    const hoveredItem = carouselRef.current?.querySelector<HTMLElement>('.styleCarouselItem:hover');

    if (hoveredItem) {
      const items = carouselRef.current?.querySelectorAll('.styleCarouselItem');
      const index = items ? Array.from(items).indexOf(hoveredItem) : -1;

      if (index !== -1) {
        hoverColorCarouselIndex(index);
      }
    }
  }, [hoverDisabled, hoverColorCarouselIndex]);

  useEffect(() => {
    const firstStyleVariant = carouselRef.current?.querySelector(
      '[data-testid="style-carousel-item"]:first-child'
    );
    const lastStyleVariant = carouselRef.current?.querySelector(
      '[data-testid="style-carousel-item"]:last-child'
    );

    if (!firstStyleVariant || !lastStyleVariant) {
      return () => {};
    }

    let isLeftArrowVisible = false;
    let isRightArrowVisible = false;

    const handleArrowVisibility = () => {
      let activeArrow: AnimationDirection | 'all' | undefined;

      if (!isLeftArrowVisible && isRightArrowVisible) {
        activeArrow = 'right';
      }

      if (isLeftArrowVisible && !isRightArrowVisible) {
        activeArrow = 'left';
      }

      if (isLeftArrowVisible && isRightArrowVisible) {
        activeArrow = 'all';
      }

      setActiveArrow(activeArrow);
    };

    const observerOptions = {
      root: carouselRef.current,
      threshold: 1.0,
      rootMargin: '1px',
    };

    const firstStyleVariantObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && isLeftArrowVisible) {
        isLeftArrowVisible = false;
        handleArrowVisibility();
      } else if (!entries[0].isIntersecting && !isLeftArrowVisible) {
        isLeftArrowVisible = true;
        handleArrowVisibility();
      }
    }, observerOptions);

    const lastStyleVariantObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && isRightArrowVisible) {
        isRightArrowVisible = false;
        handleArrowVisibility();
      } else if (!entries[0].isIntersecting && !isRightArrowVisible) {
        isRightArrowVisible = true;
        handleArrowVisibility();
      }
    }, observerOptions);

    firstStyleVariantObserver.observe(firstStyleVariant);
    lastStyleVariantObserver.observe(lastStyleVariant);

    return () => {
      firstStyleVariantObserver.disconnect();
      lastStyleVariantObserver.disconnect();
    };
  }, [styleVariants?.length]);

  const itemCount = styleVariants?.length ?? 0;

  const animate = useCallback(
    (direction: AnimationDirection, isInitialScroll = false) => {
      setHoverDisabled(true);

      if (carouselRef.current && styleVariants?.length) {
        const styleVariantWidth = carouselRef.current.scrollWidth / styleVariants.length;
        // For initial scroll the swatch of selected product should be on the 3rd
        // visible slot and 2 and a half visible swatches are visible to the left of it.
        const initialScrollRange = (productStyleVariantIndex - 2.5) * styleVariantWidth;
        const arrowScrollRange = 128;
        const leftScrollRange = isInitialScroll
          ? initialScrollRange - arrowScrollRange
          : carouselRef.current.scrollLeft;
        const availableLeftScrollRange = leftScrollRange - arrowScrollRange;
        const availableRightScrollRange = leftScrollRange + arrowScrollRange;

        if (direction === 'right') {
          carouselRef.current.scrollTo(
            Math.min(availableRightScrollRange, carouselRef.current.scrollWidth),
            0
          );
        } else {
          carouselRef.current.scrollTo(Math.max(availableLeftScrollRange, 0), 0);
        }
      }
    },
    [productStyleVariantIndex, styleVariants?.length]
  );

  useEffect(() => {
    if ((styleVariants?.length ?? 0) < 6 || productStyleVariantIndex < 3) {
      return;
    }

    animate('right', true);
  }, [animate, productStyleVariantIndex, styleVariants?.length]);

  const colorDescription = useMemo(
    () =>
      productStyleVariantIndex === styleVariantsHoveredIndex
        ? color?.description
        : styleVariants?.[styleVariantsHoveredIndex]?.mainColor || color?.description,
    [styleVariants, styleVariantsHoveredIndex, color?.description, productStyleVariantIndex]
  );

  const handleMouseEnter = useCallback(
    (index: number) => {
      if (hoverDisabled) {
        return;
      }

      hoverColorCarouselIndex(index);
    },
    [hoverDisabled, hoverColorCarouselIndex]
  );

  return (
    <S.ColorSelection data-cs-capture="" {...getColorDataAttributes(colorData)}>
      <S.ColorDescriptionWrapper>
        {colourV2 && <S.LinkLabel data-testid="color-label">{colourV2}</S.LinkLabel>}
        <S.Anchor
          href={`/shop/all?colour=${encodeURIComponent(colorDescription?.toLowerCase() ?? '')}`}
        >
          <S.ColorName data-testid="color-name">{colorDescription}</S.ColorName>
        </S.Anchor>
      </S.ColorDescriptionWrapper>

      {itemCount > 1 && (
        <S.Carousel>
          <S.ArrowLeft
            data-testid="color-arrow-left"
            onClick={() => animate('left')}
            $activeArrow={activeArrow}
            aria-hidden
            tabIndex={-1}
            type="button"
          />
          <S.ArrowRight
            data-testid="color-arrow-right"
            onClick={() => animate('right')}
            $activeArrow={activeArrow}
            aria-hidden
            tabIndex={-1}
            type="button"
          />
          <S.CarouselContainer ref={carouselRef} onScroll={handleScroll}>
            <S.CarouselInner
              data-testid="style-carousel"
              $itemCount={itemCount}
              onMouseLeave={handleMouseLeave}
            >
              {styleVariants?.map((item, index) => (
                <S.CarouselInnerItem
                  key={item?.code || index}
                  className="styleCarouselItem"
                  data-testid="style-carousel-item"
                  $isSelected={productStyleVariantIndex === index}
                  $isHovered={styleVariantsHoveredIndex === index}
                  onMouseEnter={() => handleMouseEnter(index)}
                >
                  <S.CarouselAnchor
                    href={item?.productUrl ?? '#'}
                    $isSelected={productStyleVariantIndex === index}
                    aria-label={`${accessibilityLabels?.quickView?.linkToStyleVariant}: ${
                      item?.name
                    }, ${item?.color}, ${
                      productStyleVariantIndex === index
                        ? accessibilityLabels?.accessibilityLabelSelected
                        : ''
                    }`}
                    aria-current={productStyleVariantIndex === index || undefined}
                    title={`${item?.name} ${item?.color}`}
                    {...getColorDataAttributes(item?.colorData)}
                  >
                    <Image
                      src={convertImageUrl(item?.images?.thumbnail ?? '')}
                      alt={item?.images?.thumbnailAltText ?? ''}
                      blurDataURL={imageBlurData}
                      placeholder="blur"
                      fill
                      sizes="25vw"
                    />
                  </S.CarouselAnchor>
                </S.CarouselInnerItem>
              ))}
            </S.CarouselInner>
          </S.CarouselContainer>
        </S.Carousel>
      )}
    </S.ColorSelection>
  );
};

export const ProductColor = memo(ProductColorComponent);
